import {
  CssColor,
  CssFont,
  StyleType,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParam,
  createStylesParams,
  IStyleParam,
  StyleParamType,
} from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import {
  booleanParam,
  buildStringStyleParams,
  colorParam,
  colorParamWithOpacity,
  fontParam,
  getRadiusCssValue,
  getStringStyleParamsValue,
  numberParam,
  numberParamWithMobileDefault,
  stringEnumParam,
} from './styleParamsUtils';
import { AlignmentOptions, ImageShapeOptions } from './types';

export type IStylesParams = {
  titleAlignment: StyleParamType.Font;
  serviceDescriptionTextAlign: StyleParamType.Font;
  maxCardsPerRow: StyleParamType.Number;
  cardSpacing: StyleParamType.Number;
  serviceImageOpacity: StyleParamType.Number;
  serviceImageBackgroundColor: StyleParamType.Color;
  serviceImageCornerRadius: StyleParamType.Number;
  serviceImageCornerRadii: StyleParamType.Font;
  serviceImageCornerRadiiLinked: StyleParamType.Boolean;
  isServiceImageCornerRadiusUniform: StyleParamType.Boolean;
  serviceImageBorderColor: StyleParamType.Color;
  serviceImageBorderWidth: StyleParamType.Number;
  serviceImagePadding: StyleParamType.Number;
  borderWidth: StyleParamType.Number;
  serviceImageShape: StyleParamType.Font;
  borderColor: StyleParamType.Color;
  cornerRadius: StyleParamType.Number;
  cornerRadii: StyleParamType.Font;
  cornerRadiiLinked: StyleParamType.Boolean;
  isCornerRadiusUniform: StyleParamType.Boolean;
  gridVerticalPadding: StyleParamType.Number;
  gridSidePadding: StyleParamType.Number;
  serviceDescriptionColor: StyleParamType.Color;
  serviceDescriptionFont: StyleParamType.Font;
  mobileServiceDescriptionFontSize: StyleParamType.Number;
  infoBackgroundColor: StyleParamType.Color;
  serviceNameColor: StyleParamType.Color;
  serviceNameFont: StyleParamType.Font;
  mobileServiceNameFontSize: StyleParamType.Number;
  serviceNumberColor: StyleParamType.Color;
  serviceNumberFont: StyleParamType.Font;
  readMoreColor: StyleParamType.Color;
  readMoreFont: StyleParamType.Font;
  widgetTitleFont: StyleParamType.Font;
  mobileWidgetTitleFontSize: StyleParamType.Number;
  widgetTitleColor: StyleParamType.Color;
  widgetBackgroundColor: StyleParamType.Color;
  // Card shadow styles
  cardShadowEnabled: StyleParamType.Boolean;
  cardShadowAngle: StyleParamType.Number;
  cardShadowDistance: StyleParamType.Number;
  cardShadowBlur: StyleParamType.Number;
  cardShadowColor: StyleParamType.Color;
  // display styles
  _imageDisplay: StyleParamType.Boolean;
  _serviceDescriptionDisplay: StyleParamType.Boolean;
  _serviceNumberDisplay: StyleParamType.Boolean;
  _titleDisplay: StyleParamType.Boolean;
};

const getImageAspectRatio = ({
  isMobile,
  serviceImageShape,
}: {
  isMobile: boolean;
  serviceImageShape: ImageShapeOptions;
}) =>
  isMobile
    ? serviceImageShape === ImageShapeOptions.SQUARE
      ? 1
      : 1.52
    : serviceImageShape === ImageShapeOptions.SQUARE
    ? 1
    : 1.5;

const cornerRadiiParam = (
  oldParamName: string,
): Omit<IStyleParam<StyleParamType.Font>, 'name'> => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ getStyleParamValue }) => {
    const oldParam = {
      ...numberParam(StyleType.borderRadius, 0),
      name: oldParamName,
    } as IStyleParam;
    const oldRadius = (getStyleParamValue(oldParam) as number) || 0;
    return buildStringStyleParams(
      `${oldRadius} ${oldRadius} ${oldRadius} ${oldRadius}`,
    );
  },
  metadata: {
    type: StyleType.borderRadius as StyleType.UNKNOWN_CssPropertyType,
    defaultValue: {
      type: 'static' as const,
      value: '0 0 0 0',
    },
  },
});

const serviceImageCornerRadiiLinkedParam = createStylesParam(
  'serviceImageCornerRadiiLinked',
  booleanParam(true),
);

const cornerRadiiLinkedParam = createStylesParam(
  'cornerRadiiLinked',
  booleanParam(true),
);

export const styleParamsDefinitions: {
  [key in keyof IStylesParams]: IStyleParam;
} = {
  titleAlignment: stringEnumParam<AlignmentOptions>(
    AlignmentOptions.LEFT,
    StyleType.textAlign,
  ),
  serviceDescriptionTextAlign: stringEnumParam<AlignmentOptions>(
    AlignmentOptions.LEFT,
    StyleType.textAlign,
  ),
  maxCardsPerRow: numberParamWithMobileDefault(StyleType.number, 1, 3),
  cardSpacing: numberParamWithMobileDefault(StyleType.padding, 20, 32),
  serviceImageOpacity: numberParam(StyleType.number, 100),
  serviceImageBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-5',
    0.2,
    CssColor.WstSecondaryBackgroundColor,
  ),
  serviceImageCornerRadius: numberParam(StyleType.borderRadius, 0),
  serviceImageCornerRadii: cornerRadiiParam('serviceImageCornerRadius'),
  serviceImageCornerRadiiLinked: booleanParam(true),
  isServiceImageCornerRadiusUniform: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(serviceImageCornerRadiiLinkedParam) as boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'ref' as const,
        ref: 'serviceImageCornerRadiiLinked',
      },
    },
  },
  serviceImageBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    1,
    CssColor.WstParagraph2Color,
  ),
  serviceImageBorderWidth: numberParam(StyleType.borderWidth, 0),
  serviceImagePadding: numberParam(StyleType.padding, 0),
  borderWidth: numberParam(StyleType.borderWidth, 0),
  serviceImageShape: stringEnumParam<ImageShapeOptions>(
    ImageShapeOptions.RECTANGLE,
    StyleType.stringEnum,
  ),
  borderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  cornerRadius: numberParam(StyleType.borderRadius, 0),
  cornerRadii: cornerRadiiParam('cornerRadius'),
  cornerRadiiLinked: booleanParam(true),
  isCornerRadiusUniform: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(cornerRadiiLinkedParam) as boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'ref' as const,
        ref: 'cornerRadiiLinked',
      },
    },
  },
  gridVerticalPadding: {
    inheritDesktop: false,
    ...numberParamWithMobileDefault(StyleType.padding, 20, 32),
  },
  gridSidePadding: {
    inheritDesktop: false,
    ...numberParamWithMobileDefault(StyleType.padding, 20, 0),
  },
  serviceDescriptionColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  serviceDescriptionFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'p' },
    CssFont.WstParagraph2Font,
  ),
  mobileServiceDescriptionFontSize: numberParam(StyleType.fontSize, 14),
  infoBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceNameFont: fontParam(
    'Page-title',
    { size: 24, htmlTag: 'h2' },
    CssFont.WstHeading2Font,
  ),
  serviceNameColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  mobileServiceNameFontSize: numberParam(StyleType.fontSize, 20),
  serviceNumberColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  serviceNumberFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'span' },
    CssFont.WstParagraph2Font,
  ),
  readMoreColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  readMoreFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'span' },
    CssFont.WstParagraph2Font,
  ),
  widgetTitleFont: fontParam(
    'Page-title',
    { size: 32, htmlTag: 'h1' },
    CssFont.WstHeading2Font,
  ),
  mobileWidgetTitleFontSize: numberParam(StyleType.fontSize, 24),
  widgetTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  widgetBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  // Card shadow styles
  cardShadowEnabled: booleanParam(false),
  cardShadowAngle: numberParam(StyleType.number, 90),
  cardShadowDistance: numberParam(StyleType.number, 0),
  cardShadowBlur: numberParam(StyleType.number, 25),
  cardShadowColor: colorParamWithOpacity(
    StyleType.color,
    'color-5',
    0.2,
    CssColor.WstParagraph2Color,
  ),
  // display styles
  _imageDisplay: booleanParam(true),
  _titleDisplay: booleanParam(true),
  _serviceDescriptionDisplay: booleanParam(true),
  _serviceNumberDisplay: booleanParam(true),
};

export default createStylesParams<IStylesParams>(styleParamsDefinitions);

enum DisplayStyles {
  UNSET = 'unset',
  NONE = 'none',
}

export const customCssVars: CustomCssVarsFn = (args) => {
  const { isMobile, styleParams } = args;

  const serviceImageShape = getStringStyleParamsValue(
    styleParams.strings.serviceImageShape,
  ) as ImageShapeOptions;

  const imageAspectRatio = getImageAspectRatio({
    isMobile,
    serviceImageShape,
  });

  const numericStyles = styleParams.numbers;
  const isRoundImage = serviceImageShape === ImageShapeOptions.ROUND;

  const getDisplayStyle = (key: string) => {
    const value = styleParams.booleans[key] as boolean;
    return value ? DisplayStyles.UNSET : DisplayStyles.NONE;
  };

  const getCardShadowCss = () => {
    const isEnabled = styleParams.booleans.cardShadowEnabled as boolean;
    if (!isEnabled) {
      return 'none';
    }

    const angle = numericStyles.cardShadowAngle;
    const distance = numericStyles.cardShadowDistance;
    const blur = numericStyles.cardShadowBlur;

    // Convert angle to x/y offsets (angle is in degrees, 0 is up, clockwise)
    const angleRad = ((angle - 90) * Math.PI) / 180;
    const offsetX = Math.round(Math.cos(angleRad) * distance);
    const offsetY = Math.round(Math.sin(angleRad) * distance);

    const colorStr = styleParams.colors.cardShadowColor;

    // Parse RGB/RGBA string (e.g., "rgb(0, 0, 0)" or "rgba(0, 0, 0, 0.25)")
    const rgbaMatch = colorStr.match(
      /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/,
    );
    if (rgbaMatch) {
      const r = parseInt(rgbaMatch[1], 10) || 0;
      const g = parseInt(rgbaMatch[2], 10) || 0;
      const b = parseInt(rgbaMatch[3], 10) || 0;
      const a = parseFloat(rgbaMatch[4]);
      return `${offsetX}px ${offsetY}px ${blur}px rgba(${r}, ${g}, ${b}, ${a})`;
    }
    return '';
  };

  const getServiceTitleAlignmentVars = () => {
    switch (
      getStringStyleParamsValue(
        styleParams.strings.titleAlignment,
      ) as AlignmentOptions
    ) {
      case AlignmentOptions.CENTER:
        return {
          titlePaddingLeft: '10%',
          titlePaddingRight: '10%',
          titleTextAlign: 'center',
        };
      case AlignmentOptions.LEFT:
        return {
          titlePaddingLeft: 'initial',
          titlePaddingRight: '20%',
          titleTextAlign: 'left',
        };
      case AlignmentOptions.RIGHT:
        return {
          titlePaddingLeft: '20%',
          titlePaddingRight: 'initial',
          titleTextAlign: 'right',
        };
    }
  };

  const getServiceDescriptionAlignmentVars = () => {
    switch (
      getStringStyleParamsValue(
        styleParams.strings._serviceDescriptionTextAlign,
      ) as AlignmentOptions
    ) {
      case AlignmentOptions.CENTER:
        return {
          serviceDescriptionTextAlign: 'center',
        };
      case AlignmentOptions.LEFT:
        return {
          serviceDescriptionTextAlign: 'left',
        };
      case AlignmentOptions.RIGHT:
        return {
          serviceDescriptionTextAlign: 'right',
        };
    }
  };

  return {
    // Grid styles
    maxCardsPerRow: isMobile ? 1 : numericStyles.maxCardsPerRow,
    imagePaddingTop: imageAspectRatio
      ? `${Math.round(100 / imageAspectRatio)}%`
      : `${100}%`,
    cardDisplay: 'flex',
    layoutCardMinWidth: '130px',
    infoWidth: 'inherit',
    layoutVerticalPadding: isMobile
      ? numericStyles.gridVerticalPadding
      : numericStyles.gridVerticalPadding - numericStyles.borderWidth,
    layoutSidePadding: isMobile
      ? numericStyles.gridSidePadding
      : numericStyles.gridSidePadding - numericStyles.borderWidth,
    mediaRatio: 0,
    infoRatio: 1,
    infoOverflow: 'unset',
    imageOpacity: numericStyles.serviceImageOpacity / 100,
    imageBorderWidth: numericStyles.serviceImageBorderWidth,
    imageWrapperRadius: isRoundImage
      ? '50%'
      : getRadiusCssValue(styleParams.strings.serviceImageCornerRadii),
    imageCardCornerRadius: 0,
    imageWrapperMargin: isRoundImage
      ? `${32 - numericStyles.borderWidth}px auto 0`
      : `${numericStyles.serviceImagePadding}px`,
    imageWrapperHeight: `calc(100% - ${
      isRoundImage ? 32 : numericStyles.serviceImagePadding * 2
    }px)`,
    imageWrapperWidth: isRoundImage
      ? `calc(${100 / imageAspectRatio}% - 32px)`
      : `calc(100% - ${numericStyles.serviceImagePadding * 2}px)`,
    cardBorderWidth: numericStyles.borderWidth,
    stripCalcIndication: '0px',
    nonStripCalcIndication: '1px',
    mediaFlex: 0,
    infoFlex: 1,
    backgroundOnHover: 'inherit',
    cornerRadius: getRadiusCssValue(styleParams.strings.cornerRadii),
    infoCardCornerRadius: 0,
    gridTemplateRows: 'unset',
    infoBorderWidth: 0,
    infoZIndex: 'auto',
    rootBackgroundColor: styleParams.colors.infoBackgroundColor,
    evenImageDirection: 'column',
    oddImageDirection: 'column',
    evenOverlappingImageMargin: 0,
    oddOverlappingImageMargin: 0,
    evenOverlappingGridArea: 'unset',
    oddOverlappingGridArea: 'unset',
    evenOverlappingTemplateColumns: 'unset',
    oddOverlappingTemplateColumns: 'unset',
    mediaWidth: '100%',
    mediaHeight: '100%',
    ...getServiceTitleAlignmentVars(),
    // ...getServiceDescriptionAlignmentVars(),
    // display styles
    imageDisplay: getDisplayStyle('_imageDisplay'),
    titleDisplay: getDisplayStyle('_titleDisplay'),
    serviceDescriptionDisplay: getDisplayStyle('_serviceDescriptionDisplay'),
    serviceNumberDisplay: getDisplayStyle('_serviceNumberDisplay'),
    // Card shadow
    cardShadow: getCardShadowCss(),
  };
};
